import React, { forwardRef, PropsWithChildren } from "react";

type SpaceDirection = "horizontal" | "vertical";
type SpaceSizeValue = number | string;
type SpaceSize = SpaceSizeValue | [SpaceSizeValue, SpaceSizeValue];
type SpaceAlign =
  | React.CSSProperties["alignItems"]
  | "start"
  | "end";
type CompatibleClassName =
  | string
  | false
  | null
  | undefined
  | CompatibleClassName[];

export interface SpaceUIProps
  extends PropsWithChildren<
    Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "className">
  > {
  direction?: SpaceDirection;
  size?: SpaceSize;
  align?: SpaceAlign;
  wrap?: boolean;
  split?: React.ReactNode;
  className?: CompatibleClassName;
}

const namedSizeMap: Record<string, string> = {
  mini: "4px",
  small: "8px",
  medium: "16px",
  large: "24px",
};

const toGapValue = (size: SpaceSizeValue) => {
  if (typeof size === "number") {
    return `${size}px`;
  }

  return namedSizeMap[size] ?? size;
};

const toGapStyle = (size: SpaceSize = "small"): React.CSSProperties => {
  if (Array.isArray(size)) {
    return {
      rowGap: toGapValue(size[0]),
      columnGap: toGapValue(size[1]),
    };
  }

  return {
    gap: toGapValue(size),
  };
};

const toAlignItems = (align: SpaceAlign | undefined) => {
  if (align === "start") {
    return "flex-start";
  }

  if (align === "end") {
    return "flex-end";
  }

  return align;
};

const toClassName = (className: CompatibleClassName): string | undefined => {
  if (Array.isArray(className)) {
    return className.map(toClassName).filter(Boolean).join(" ");
  }

  return className || undefined;
};

const renderItems = (children: React.ReactNode, split: React.ReactNode) => {
  const childItems = React.Children.toArray(children);
  const hasSplit = split !== undefined && split !== null;

  return childItems.flatMap((child, index) => {
    const item = (
      <div className="space-ui-item" key={`item-${index}`}>
        {child}
      </div>
    );

    if (!hasSplit || index === childItems.length - 1) {
      return [item];
    }

    return [
      item,
      <span className="space-ui-split" key={`split-${index}`}>
        {split}
      </span>,
    ];
  });
};

const SpaceUI = forwardRef<HTMLDivElement, SpaceUIProps>((props, ref) => {
  const {
    children,
    direction = "horizontal",
    size,
    className,
    style,
    align,
    wrap,
    split,
    ...restProps
  } = props;
  const resolvedAlign =
    align ?? (direction === "horizontal" ? "center" : undefined);
  const mergedClassName = [
    "space-ui",
    `space-ui-${direction}`,
    wrap ? "space-ui-wrap" : undefined,
    toClassName(className),
  ]
    .filter(Boolean)
    .join(" ");
  const rootStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: direction === "vertical" ? "column" : "row",
    alignItems: toAlignItems(resolvedAlign),
    flexWrap: wrap ? "wrap" : undefined,
    ...toGapStyle(size),
    ...style,
  };

  return (
    <div {...restProps} className={mergedClassName} ref={ref} style={rootStyle}>
      {renderItems(children, split)}
    </div>
  );
});

SpaceUI.displayName = "SpaceUI";

export default SpaceUI;
